import type { ChartLayout } from "../types.js";
import { ensureAn26DesignStyles } from "./injectStyles.js";
import {
  SERIES_COLORS,
  SERIES_FADED_COLORS,
  type LineChartAxisEdgeBound,
  type LineChartPayload,
  type LineChartTableRow,
  type LineChartYAxisBounds,
} from "../normalize.js";
import { datetimeTicks, datetimeTickLabel } from "./geometry/axisTicks.js";
import { lineChartPlotWidth } from "./geometry/plotWidth.js";
import { renderD3LineChart } from "./d3/line.js";
import { formatBarDisclaimerHtml } from "./richText.js";

const COMPACT_LINE_CHART_MAX_WIDTH = 560;
/** Min plot width on narrow viewports; whole chart (incl. Y-axis) scrolls in `.an26-line-chart__scroll`. */
const LINE_CHART_SCROLL_MIN_WIDTH = 420;
const LINE_CHART_HEADROOM_RATIO = 0.06;
const LINE_CHART_HEADROOM_MIN = 10_000;
const END_LABEL_MIN_GAP = 18;

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fmtUsd(n: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
}

function cellFmt(v: number | string | null | undefined): string {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  if (typeof v === "number" && Number.isFinite(v)) return `${v.toFixed(2)}`;
  return "—";
}

function buildTableHtml(data: LineChartPayload, inceptionDate: string | undefined): string {
  const td = data.tableData;
  if (!td || !td.periods.length || !td.rows.length) return "";
  const corner = `Month End (As of ${esc(data.asOf)})`;
  const periodHeads = td.periods
    .map((p, i) => {
      const isLast = i === td.periods.length - 1;
      const sub =
        isLast && inceptionDate
          ? `<br/><span class="an26-table__th-sub">${esc(inceptionDate)}</span>`
          : "";
      return `<th>${esc(p)}${sub}</th>`;
    })
    .join("");
  const nameToSeriesIndex = new Map(data.series.map((s, i) => [s.name, i]));
  const rows = td.rows
    .map((r: LineChartTableRow) => {
      const si = nameToSeriesIndex.get(r.name) ?? 0;
      const color = SERIES_COLORS[si % SERIES_COLORS.length];
      const cells = td.periods.map((_, pi) => `<td>${esc(cellFmt(r.values[pi]))}</td>`).join("");
      return `<tr>
        <td class="an26-table__label"><span class="an26-table__swatch an26-table__swatch--line" style="background:${esc(color)}"></span>${esc(r.name)}</td>
        ${cells}
      </tr>`;
    })
    .join("");
  return `<div class="an26-table-wrap"><table class="an26-table an26-table--line" role="grid">
    <thead><tr><th class="an26-table__corner">${corner}</th>${periodHeads}</tr></thead>
    <tbody>${rows}</tbody>
  </table></div>`;
}

function compactTickPositions(ticks: number[]): number[] {
  if (ticks.length <= 4) return ticks;
  return ticks.filter((_, i) => i === 0 || i === ticks.length - 1 || i % 2 === 0);
}

/** Pick a human-friendly USD step (25k, 50k, 100k, …) for ~5 y-axis ticks. */
function niceUsdStep(range: number, targetTickCount = 5): number {
  if (range <= 0) return 25_000;
  const rough = range / targetTickCount;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const residual = rough / magnitude;
  const niceUnit =
    residual <= 1.5 ? 1 : residual <= 3 ? 2 : residual <= 7 ? 5 : 10;
  return niceUnit * magnitude;
}

function defaultLineChartYMax(dataMax: number): number {
  return dataMax + Math.max(dataMax * LINE_CHART_HEADROOM_RATIO, LINE_CHART_HEADROOM_MIN);
}

function resolveAxisEdge(
  edge: LineChartAxisEdgeBound | undefined,
  role: "min" | "max",
  dataMin: number,
  dataMax: number
): number | undefined {
  if (!edge) return undefined;
  if (edge.fixed !== undefined) return edge.fixed;
  if (edge.padding) {
    const extreme = role === "min" ? dataMin : dataMax;
    const ratio = edge.padding.ratio ?? 0;
    const minPadding = edge.padding.minPadding ?? 0;
    const amount = Math.max(ratio * extreme, minPadding);
    return role === "min" ? dataMin - amount : dataMax + amount;
  }
  return undefined;
}

function lineChartYAxisScale(
  allY: number[],
  bounds?: LineChartYAxisBounds
): { yMin: number; yMax: number; tickPositions: number[] } {
  const dataMin = allY.length ? Math.min(...allY) : 0;
  const dataMax = allY.length ? Math.max(...allY) : 100_000;

  let yMin = resolveAxisEdge(bounds?.min, "min", dataMin, dataMax);
  if (yMin === undefined) yMin = 0;

  let yMax = resolveAxisEdge(bounds?.max, "max", dataMin, dataMax);
  if (yMax === undefined) yMax = defaultLineChartYMax(dataMax);

  if (yMin >= yMax || !Number.isFinite(yMin) || !Number.isFinite(yMax)) {
    console.warn("nten-data: invalid yAxisBounds; using default line chart Y-axis scale.");
    yMin = 0;
    yMax = defaultLineChartYMax(dataMax);
  }

  yMin = Math.min(yMin, dataMin);
  yMax = Math.max(yMax, dataMax);

  const step = niceUsdStep(yMax - yMin);
  const yMaxRounded = Math.ceil(yMax / step) * step;
  const yMinRounded = bounds?.min?.fixed !== undefined ? yMin : Math.floor(yMin / step) * step;

  const tickPositions: number[] = [];
  for (let v = yMinRounded; v <= yMaxRounded + 1e-6; v += step) {
    tickPositions.push(v);
  }

  return { yMin: yMinRounded, yMax: yMaxRounded, tickPositions };
}

export interface LineChartRenderOptions {
  title?: string | null;
  subtitle?: string | null;
  disclaimer?: string | null;
  layout?: ChartLayout;
}

export function renderLineChart(
  container: HTMLElement,
  data: LineChartPayload,
  opts?: LineChartRenderOptions
): () => void {
  ensureAn26DesignStyles();
  const embedded = opts?.layout === "embedded";
  const title = opts?.title ?? "Cumulative Total Returns";
  const subtitle = opts?.subtitle ?? (data.asOf ? `As of ${data.asOf}` : "");
  const inception = data.inceptionDate;
  const disclaimerRaw =
    opts?.disclaimer === null ? "" : (opts?.disclaimer ?? data.disclaimer ?? "");
  const disclaimer =
    !embedded && disclaimerRaw
      ? formatBarDisclaimerHtml(disclaimerRaw, data.benchmarkName)
      : "";
  const chartHostId = `an26-line-hc-${Math.random().toString(36).slice(2, 9)}`;
  const head = embedded
    ? ""
    : `<header class="an26-root__head">
        <h2 class="an26-root__title">${esc(String(title))}</h2>
        ${subtitle ? `<p class="an26-root__subtitle">${esc(subtitle)}</p>` : ""}
      </header>`;
  const rootClass = embedded ? "an26-root an26-root--line an26-root--embedded" : "an26-root an26-root--line";

  container.innerHTML = `
    <div class="${rootClass}">
      ${head}
      <div class="an26-root__chart-wrap">
        <div class="an26-line-chart__scroll">
          <div id="${chartHostId}" class="an26-root__chart an26-line-chart__plot"></div>
        </div>
      </div>
      ${buildTableHtml(data, inception)}
      ${disclaimer ? `<p class="an26-line__disc">${disclaimer}</p>` : ""}
    </div>`;

  const mount = container.querySelector(`#${chartHostId}`) as HTMLElement | null;
  if (!mount || !data.series.length) {
    return () => container.replaceChildren();
  }

  const allY = data.series.flatMap((s) => s.data.map((d) => d[1])).filter((n) => Number.isFinite(n));
  const { yMin, yMax, tickPositions } = lineChartYAxisScale(allY, data.yAxisBounds);
  const compactTicks = compactTickPositions(tickPositions);

  // Only width-independent values are passed in; the renderer derives breakpoint,
  // spacing, height and x ticks from its own width on every draw, so a resize
  // recomputes them.
  const cleanup = renderD3LineChart(mount, data, {
    yAxis: {
      min: yMin,
      max: yMax,
      ticks: tickPositions,
      compactTicks,
      formatLabel: fmtUsd,
    },
    embedded,
    esc,
  });
  return () => {
    cleanup();
    container.replaceChildren();
  };
}
